import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {actions, initialState} from './actions';
import {reducer} from './CMWorklist.reducer';
import {useAuth} from '../../../../../../context/authContext';
import {GetCall} from './api';
import {useEQHToastContext} from "../../../../../../components/toast/EQHToast.context";
import {getGetters} from './getters';
import {getToggles} from './toggles';
import {useMutation, useQuery} from 'react-query';
import {getDropListByName} from '../../../../../../actions/constantsActions';
import {isEqual} from 'lodash';
import {getQuickListNameByRole} from '../../../utils';
import {SessionStorageUtil} from '../../../../../../utils/localStorageUtil';
import {useLocation} from "react-router-dom";
import {TRACK_EVENTS, trackPendoEvent} from "../../../../../../utils/pendoTrack";

const CMWorklistContext = createContext();

const RequestInfo = {
  isCalling: false
}

const EVENT_ACTION_TO_PENDO_EVENT = {
  SUBMIT_ASSIGN_PATIENT: TRACK_EVENTS.CM_ASSIGN_PATIENT_SUBMIT,
  CONFIRM_SAVE_OUTREACHED: TRACK_EVENTS.CM_MARK_OUTREACHED_SUBMIT,
  CONFIRM_REASSIGN_CARE_STEP: TRACK_EVENTS.CM_REASSIGN_CARE_STEP_SUBMIT,
  CONFIRM_SAVE_ASSESMENT: TRACK_EVENTS.CM_ASSESSMENT_SAVE,
  CONFIRM_REMOVE_WORKLIST: TRACK_EVENTS.CM_REMOVE_WORKLIST_SUBMIT,
  SAVE_DEFAULT: TRACK_EVENTS.CM_SAVE_DEFAULT_VIEW,
  EXPORT_CSV: TRACK_EVENTS.CM_EXPORT_CSV,
  CLICK_SAVE_WORKLIST: TRACK_EVENTS.QUICKLIST_CUSTOM_WORKLIST_SAVE,
  DELETE_WORKLIST: TRACK_EVENTS.QUICKLIST_CUSTOM_WORKLIST_DELETE,
  SELECT_PATIENT: TRACK_EVENTS.QUICKLIST_PATIENT_SELECTED,
  SELECT_UPDATE_REFERRAL: TRACK_EVENTS.REFERRAL_SUBMIT_OPEN,
  CONFIRM_SAVE_REFERRAL: TRACK_EVENTS.REFERRAL_STATUS_UPDATE
};

const CMWorklistProvider = ({ children }) => {
  const auth = useAuth();
  const authorizedCMWorklistAccess = auth.hasAnyAuthority(["AUTH_CM_WORKLIST_ACCESS"]);
  const globalDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);
  const getters = getGetters(state);
  const toggles = getToggles(dispatch, globalDispatch);
  const role = (state.activeRole.role) ?? auth.getRoles()[0];
  const reloadWorklist = useSelector((state) => state?.manageVisitReducer?.reloadWorklist);
  const location = useLocation();

  const { removeNotification, setNotification } = useEQHToastContext();

  const GetUrlByRole = (...args) => {
    const role = (state.activeRole.role) ?? auth.getRoles()[0];
    return GetCall(role, ...args)
  }

  const { refetch: getCareManagementTeamsQuery } = useQuery({
    queryKey: ['careManagementTeams'],
    queryFn: () => {
      GetUrlByRole('careManagementTeams', '')
        .then((response) => {
          dispatch({ type: actions.GET_CARE_MANAGEMENT_TEAMS, payload: response.data.map(team => team.name) })
        })
        .catch(err => setCustomWorkListError(["Error", err?.response?.data?.message || err?.message]))
    },
    refetchOnWindowFocus: false,
    manual: true,
    enabled: false
  })

  const { refetch: getAssignPatient } = useQuery({
    queryKey: ['assignPatient'],
    queryFn: () => callbacks?.assignPatientRequest(state?.assignPayload, false),
    refetchOnWindowFocus: false,
    manual: true,
    enabled: false
  })

  const searchPatientFn = (adtRequestType = null) => {
    RequestInfo.isCalling = true;
    dispatch({ type: actions.SET_SELECTED_PATIENTS, payload: [] });
    dispatch({ type: actions.SET_LOADING, payload: true });
    const { leadId } = state.activeRole;
    const params = leadId ? `?programLeadId=${leadId}` : '';
    const sessionObj = SessionStorageUtil.getObj(adtRequestType === "COUNT" ? 'lastMembersQueryCount' : 'lastMembersQueryValues') || []
    let payload = {
      pageSize: 20,
      quickListName: "CM_WORKLIST_HUB_COORDINATOR",
      sortParams: [],
      ...state.patientSearch,
    };
    if (SessionStorageUtil.getObj('backing') && sessionObj && !leadId) {
      payload = {...JSON.parse(sessionObj)}
      toggles.setPatientSearch({ ...JSON.parse(sessionObj) });
      toggles.setPage(payload?.pageNumber)
    } else {
      adtRequestType === 'COUNT' && SessionStorageUtil.setObj('lastMembersQueryCount', JSON.stringify(payload));
      adtRequestType === 'VALUES' && SessionStorageUtil.setObj('lastMembersQueryValues', JSON.stringify(payload));
      toggles.setPatientSearch({ ...payload });
    }
    const filterParams = Array.isArray(payload.filterParams) ? payload.filterParams : [];
    payload.filterParams = filterParams.filter(value => value && Object.keys(value).length !== 0);
    return GetUrlByRole('searchPatient', params, { ...payload, adtRequestType });
  }

  const getPatientValues = useMutation(async () => {
    return await searchPatientFn('VALUES');
  });

  const getPatientCount = useMutation(async () => {
    return await searchPatientFn('COUNT');
  });

  const getSearchPatient = useMutation(async () => {
    const [patientValuesResponse, patientCountResponse] = await Promise.all([
      getPatientValues.mutateAsync(),
      getPatientCount.mutateAsync()
    ]);
    return {
      data: {
        ...patientValuesResponse?.data,
        totalSize: patientCountResponse?.data?.totalSize
      }
    };
  }, {
    onSuccess: (res) => {
      dispatch({ type: actions.SET_PATIENTS_LIST, payload: res?.data?.patientCardDtos });
      dispatch({ type: actions.SET_LOADING, payload: false });
      dispatch({ type: actions.SET_TOTAL_SIZE, payload: res?.data?.totalSize });
      dispatch({ type: actions.ASSIGN_PATIENT, payload: false });
      const params = new URLSearchParams(location.search)

      if (SessionStorageUtil.getObj('backing') && !params.get('customListName')) {
        document.getElementById('filter-button').click()
        document.getElementById('apply-button').click()
      }
    },
    onError: (err) => {
      dispatch({ type: actions.SET_LOADING, payload: false });
    },
    onSettled: () => {
      RequestInfo.isCalling = false;
      const params = new URLSearchParams(location.search)
      if (SessionStorageUtil.getObj('backing') && params.get('customListName')) {
        setTimeout(() => {
          document.getElementById('filter-button').click()
          document.getElementById('apply-button').click()
        }, 1000);
      }
    }
  });

  const useDeepCompareMemoize = (value) => {
    const ref = useRef();
    if (!isEqual(value, ref.current)) {
      ref.current = value;
    }
    return ref.current;
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(), delay);
    };
  };

  const memoizedFilterParams = useMemo(() => state?.patientSearch?.filterParams, [state.patientSearch.filterParams]);
  const memoizedSortParams = useMemo(() => state?.patientSearch?.sortParams, [state.patientSearch.sortParams]);
  const deepCompareFilterParams = useDeepCompareMemoize(memoizedFilterParams);
  const deepCompareSortParams = useDeepCompareMemoize(memoizedSortParams);
  const activeRole = state?.activeRole;
  const activeRoleCustom = activeRole?.custom;
  const activeRoleLeadId = activeRole?.leadId;
  const patientSearchPageNumber = state?.patientSearch?.pageNumber;
  const patientSearchReload = state?.patientSearch?.reload;
  const careManagementTeamsLength = state?.careManagementTeams?.length;
  const adminRolesHubWLLength = state?.adminRolesHubWL?.length;
  const debouncedGetPatientsList = useCallback(debounce(() => getPatientsListAsync(), 300), []);

  useEffect(() => {
    const shouldFetchPatients =
      !activeRoleCustom &&
      memoizedFilterParams !== null &&
      authorizedCMWorklistAccess &&
      careManagementTeamsLength !== 0 &&
      adminRolesHubWLLength !== 0 &&
      (!RequestInfo.isCalling);
    if (shouldFetchPatients || reloadWorklist) {
      RequestInfo.isCalling = true;
      debouncedGetPatientsList();
    } else if (activeRoleCustom) {
      setActiveRole({ ...activeRole, custom: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deepCompareFilterParams,
    patientSearchPageNumber,
    deepCompareSortParams,
    patientSearchReload,
    activeRoleCustom,
    activeRoleLeadId,
    debouncedGetPatientsList,
    authorizedCMWorklistAccess,
    careManagementTeamsLength,
    adminRolesHubWLLength,
    reloadWorklist
  ]);

  useEffect(() => {
    toggles.toggleIsLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getPatientsListAsync = async () => {
    state?.assignPayload && await getAssignPatient()
    getSearchPatient.mutate()
  }

  const setExportSummaryData = (exportSummaryData) => {
    dispatch({ type: actions.SET_EXPORT_SUMMARY_DATA, payload: exportSummaryData })
  }

  const setCustomWorkListError = (errorInfo) => {
    if (errorInfo?.length > 1) {
      setNotification(errorInfo[0], errorInfo[1]);
    } else {
      removeNotification();
    }
  }

  const setActiveRole = (activeRole) => {
    dispatch({ type: actions.SET_ACTIVE_ROLE, payload: activeRole })
  }

  const sendEvent = async (action) => {
    const eventName = EVENT_ACTION_TO_PENDO_EVENT[action];
    if (!eventName) return;

    const quickListName = getQuickListNameByRole(role) || "CM_WORKLIST_HUB_COORDINATOR";
    trackPendoEvent(eventName, {
      route: location?.pathname || window?.location?.pathname || "/",
      quicklist_type: quickListName,
      user_role: role,
      source_module: "CMWorklist.context",
      action_type: action
    });
  };

  const callbacks = {
    setCustomWorkListError: setCustomWorkListError,
    setActiveRole: setActiveRole,
    saveActivity: async (patientData) => await GetUrlByRole('saveActivity', '', patientData)
      .then(() => {
      }),
    setExportSummaryData: setExportSummaryData,
    getPatientsList: getPatientsListAsync,
    removePatient: async (patientData) => await GetUrlByRole('saveActivity', '', patientData)
      .then(() => {
        dispatch({ type: actions.TOGGLE_REMOVE_FROM_WORKLIST, payload: false })
        toggles.setPatientSearch({ ...state.patientSearch, reload: !state?.patientSearch?.reload })
      }).catch(err => {
        if (err?.response?.data?.message === 'PATIENT_NOT_FOUND') {
          dispatch({ type: actions.TOGGLE_REMOVE_FROM_WORKLIST, payload: false })
          dispatch({ type: actions.PATIENT_HAS_ALREADY_BEEN_REMOVED, payload: true })
        }
      }),
    getCarePaths: async (id) => await GetUrlByRole('carePathsOpen', id)
      .then(carePaths => {
        dispatch({ type: actions.SELECT_CARE_PATHS, payload: carePaths.data })
      })
      .catch(err => setCustomWorkListError(["Error", err?.response?.data?.message || err?.message])),
    getCarePrograms: async () => await GetUrlByRole('carePrograms', '')
      .then(carePrograms => {
        dispatch({ type: actions.SELECT_CARE_PATHS, payload: carePrograms.data })
      })
      .catch(err => setCustomWorkListError(["Error", err?.response?.data?.message || err?.message])),
    getDeclineReasons: async () => await GetUrlByRole('declinedReasons', '')
      .then(declineReasons => {
        dispatch({ type: actions.DECLINE_REASONS, payload: declineReasons.data })
      }),
    exportToCSV: (filters, sortParams, selectedPatientsEventsKeys) =>
      GetUrlByRole('exportToCSV', '', filters, sortParams, selectedPatientsEventsKeys)
        .then((response) => {
          GetUrlByRole('exportSummary', '')
            .then((response) => {
              setExportSummaryData(response.data);
            })
        }),
    exportToActivator: (filters, sortParams, selectedPatientsEventsKeys) =>
      GetUrlByRole('exportToActivator', '', filters, sortParams, selectedPatientsEventsKeys)
        .then((response) => {
          GetUrlByRole('exportSummary', '')
            .then((response) => {
              setExportSummaryData(response.data);
            })
        }),
    selectPatientEventAction: payload => dispatch({ type: actions.SET_SELECTED_PATIENTS, payload: payload }),
    getTeamsCapacity: async () => await GetUrlByRole('teamCapacity', '')
      .then((response) => {
        dispatch({ type: actions.GET_TEAMS_CAPACITY, payload: response.data })
      })
      .catch(err => setCustomWorkListError(["Error", err?.response?.data?.message || err?.message])),
    getCareManagementTeams: getCareManagementTeamsQuery,
    assignPatient: (payload) => {
      dispatch({ type: actions.SET_ASSIGN_PAYLOAD, payload: payload })
      toggles.toggleAssignPatientTo(false)
      toggles.toggleUpdateReferralStatus(true)
    },
    assignPatientRequest: async (assignPayload, isAssigned = true) => {
      dispatch({ type: actions.SET_ASSIGN_PAYLOAD, payload: null })
      await GetUrlByRole('assignPatient', '', assignPayload)
        .then(() => {
          dispatch({ type: actions.ASSIGN_PATIENT, payload: isAssigned });
        })
        .catch(err => {
          setCustomWorkListError(["Error", err?.response?.data?.message || err?.message])
        })
    },
    getPatientTemplates: async (id) => await GetUrlByRole('patientTemplates', id)
      .then(patientTemplates => {
        toggles.setPatientTemplates(patientTemplates.data);
      }),
    saveAssessment: async (assessmentData) => {
      toggles.toggleIsSavingAssessment(true);
      return await GetUrlByRole('saveAssessment', '', assessmentData)
        .then(() => {
          toggles.toggleIsSavingAssessment(false);
        })
        .catch(err => setCustomWorkListError(["Error", err?.response?.data?.message || err?.message]))
    },
    getWorklistDefaultView: async () => {
      const { leadId } = state.activeRole;

      return await GetUrlByRole('worklistDefaultView', '', leadId)
        .then(worklistDefaultView => {
          let worklistDefaultViewData = null;

          if (worklistDefaultView.data === '') {
            worklistDefaultViewData = {
              id: '',
              quickList: getQuickListNameByRole(role),
              patientRequestDTO: {
                filterParams: [
                  {
                    paramName: 'stepState',
                    paramValue: [
                      'ACTIVE'
                    ],
                    comparison: 'eq',
                    displayValue: [
                      'ACTIVE'
                    ]
                  }
                ],
                sortParams: [],
                pageSize: 0,
                quickListName: null,
                quickFilterName: null,
                previousTotalSize: 0
              }
            }
          } else if (worklistDefaultView.data) {
            worklistDefaultViewData = worklistDefaultView.data;
          }
          toggles.setWorklistDefaultView(worklistDefaultViewData);
          toggles.setPatientSearch({ ...state.patientSearch, filterParams: worklistDefaultViewData?.patientRequestDTO?.filterParams })
        }).catch(() => {
          toggles.toggleIsLoading(false);
          toggles.setWorklistDefaultView(null);
        })
    },
    saveWorklistDefaultView: async (worklistDefaultViewData) => {
      removeNotification();
      return await GetUrlByRole('saveWorklistDefaultView', '', worklistDefaultViewData)
        .then(response => {
          response?.data?.id && setNotification('Successful', 'Custom worklist saved successfully.');
        }).catch(err => {
          removeNotification();
        })
    },
    getCampaigns: async () => await GetUrlByRole('campaigns', '')
      .then(response => {
        dispatch({ type: actions.SET_CAMPAIGNS, payload: response.data })
      }),
    markAsOutreached: async (data) => await GetUrlByRole('saveActivity', '', data)
      .then(() => {
        toggles.toggleMarkAsOutreached(false)
        toggles.setPatientsEventsToBeMarkedAsOutreached([])
        toggles.setPatientSearch({ ...state.patientSearch });
      }).catch(err => {
        if (err?.response?.data?.message === 'PATIENT_NOT_FOUND') {
          toggles.toggleMarkAsOutreached(false)
          dispatch({ type: actions.PATIENT_HAS_ALREADY_BEEN_OUTREACHED, payload: true })
        }
      }),
    getAdminRolesHubWL: async () => await getDropListByName('ADMIN_ROLES_HUB_WL')
      .then(adminRolesHubWLDropList => toggles.setAdminRolesHubWL(adminRolesHubWLDropList.data.map(item => item.text))),
    sendEvent: async (action) => sendEvent(action)
  }

  const value = {
    ...getters,
    ...toggles,
    ...callbacks
  }

  return (
    <CMWorklistContext.Provider value={value}>
      {children}
    </CMWorklistContext.Provider>
  )
};
const useCMWorklistContext = () => useContext(CMWorklistContext);

export {
  CMWorklistProvider,
  useCMWorklistContext,
}
